/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import getConfig from '../config';
import { getLogger } from '../utils/SearchLogger';
import WebStorage from '../storage/WebStorage';
import Request from '../utils/Request';

interface GradientItem {
	primaryColor: RGBColor;
	secondaryColor: RGBColor;
}

type GradientsData = Map<string, GradientItem>;

interface GradientsDataSerialized {
	gradients: Array<[string, GradientItem]>;
}

interface GradientApiResponse {
	default: string;
	children: Array<{
		'project:gradientId': string;
		'project:gradientData': {
			stops: Array<{
				color: Array<{
					value: RGBColor;
				}>;
			}>;
		};
		'project:gradientTextColor': {
			value: RGBColor;
		};
	}>;
}

class ProjectsService {
	/*
	 **
	 **  P U B L I C   I N T E R F A C E
	 **
	 */

	// Track in-flight request to prevent duplicate API calls
	private static _inflightRequest: Promise<GradientsData> | null = null;

	/**
	 * Retrieves gradients data from the Projects API.
	 * Returns cached data if available, otherwise fetches from server.
	 * Deduplicates concurrent requests.
	 */
	async fetchGradients(forceFetchFromServer = false): Promise<GradientsData> {
		// Check if there's already a request in-flight
		if (ProjectsService._inflightRequest && !forceFetchFromServer) {
			// Return the existing promise to avoid duplicate requests
			return ProjectsService._inflightRequest;
		}

		// Create a new promise for this request
		const requestPromise = this._fetchGradientsInternal(forceFetchFromServer);

		// Store the in-flight request
		ProjectsService._inflightRequest = requestPromise;

		// Clean up after the request completes (success or failure)
		requestPromise
			.then(() => {
				ProjectsService._inflightRequest = null;
			})
			.catch(() => {
				ProjectsService._inflightRequest = null;
			});

		return requestPromise;
	}

	/**
	 * Internal method that performs the actual gradient fetching logic
	 */
	private async _fetchGradientsInternal(forceFetchFromServer = false): Promise<GradientsData> {
		let gradientsData: GradientsData = new Map();

		try {
			// Try to get from sessionStorage
			const cachedData = WebStorage.sessionStorage.getItem('gradientsData');

			if (!forceFetchFromServer && typeof cachedData === 'object' && cachedData !== null) {
				// Deserialize the Map from storage
				const deserializedData = new Map<string, GradientItem>(cachedData.gradients);
				return deserializedData;
			}

			// Fetch fresh data
			gradientsData = await this._performGradientsRequest();

			if (gradientsData && gradientsData.size > 0) {
				// Serialize the Map for storage
				const serializedData: GradientsDataSerialized = {
					gradients: Array.from(gradientsData.entries()),
				};
				WebStorage.sessionStorage.setItem('gradientsData', serializedData);
			}

			return gradientsData;
		} catch (e) {
			const errorMessage = `Failed to fetch gradients from Projects API: ${e instanceof Error ? e.message : String(e)}`;
			this.logger.error('ProjectsService', errorMessage, e);

			// Throw a clear error to inform calling code
			throw new Error(errorMessage);
		}
	}

	searchHostContext: HostContext;
	logger: Logger;

	constructor(searchHostContext: HostContext) {
		this.logger = getLogger();
		this.searchHostContext = searchHostContext;
	}

	/**
	 * Performs the HTTPS request to retrieve gradients from Projects API.
	 * @returns {Promise<GradientsData>}
	 */
	private async _performGradientsRequest(): Promise<GradientsData> {
		try {
			const hostname = getConfig(this.searchHostContext.environment, 'PROJECTS_SERVER');
			const url = `https://${hostname}/api/v3/gradients`;

			const headers = {
				'Content-Type': 'application/json',
				'x-api-key': this.searchHostContext.hostInfo.apiKey,
				authorization: `Bearer ${this.searchHostContext.imsInfo.accessToken}`,
			};

			const response = await Request.withTimeout(5000).withHeaders(headers).send('GET', url);

			if (response.isError()) {
				throw new Error(`_performGradientsRequest failed, statusCode=${response.status()}`);
			}

			const responseData = response.data() as GradientApiResponse;
			const gradientsMap = new Map<string, GradientItem>();

			// Transform the response into our desired structure
			if (responseData.children && Array.isArray(responseData.children)) {
				responseData.children.forEach((child) => {
					const gradientId = child['project:gradientId'];
					const stops = child['project:gradientData']?.stops;
					const textColor = child['project:gradientTextColor']?.value;

					// Linear gradients from Projects API contain 2 stops (offset 0 and offset 1).
					// Stop at offset 1 typically contains the darker/more saturated color,
					// which provides better visual contrast for project card backgrounds.
					if (gradientId && stops && stops.length >= 2 && textColor) {
						const primaryColor = stops[1].color?.[0]?.value;

						if (primaryColor) {
							gradientsMap.set(gradientId, {
								primaryColor,
								secondaryColor: textColor,
							});
						}
					}
				});
			}

			return gradientsMap;
		} catch (error) {
			throw new Error(`ProjectsService connection error: ${error.toString()}`);
		}
	}
}

export default ProjectsService;
